/**
 * Isometric coordinate utilities
 * Handles conversion between world (grid) and screen (pixel) coordinates
 */

/** Isometric tile dimensions */
export const TILE_WIDTH = 64;
export const TILE_HEIGHT = 32;

/**
 * Convert world (grid) coordinates to screen (pixel) coordinates
 * @param worldX Grid X position
 * @param worldY Grid Y position
 * @param worldZ Height/elevation (optional)
 * @returns Screen position { x, y }
 */
export function worldToScreen(
  worldX: number,
  worldY: number,
  worldZ: number = 0
): { x: number; y: number } {
  return {
    x: (worldX - worldY) * (TILE_WIDTH / 2),
    y: (worldX + worldY) * (TILE_HEIGHT / 2) - worldZ,
  };
}

/**
 * Convert screen (pixel) coordinates to world (grid) coordinates
 * Note: Cannot determine Z from screen position alone
 * @param screenX Pixel X position
 * @param screenY Pixel Y position
 * @returns World position { x, y }
 */
export function screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
  return {
    x: (screenX / (TILE_WIDTH / 2) + screenY / (TILE_HEIGHT / 2)) / 2,
    y: (screenY / (TILE_HEIGHT / 2) - screenX / (TILE_WIDTH / 2)) / 2,
  };
}

/**
 * Get tile coordinates from pixel position (rounded to nearest tile)
 * @param pixelX Pixel X position
 * @param pixelY Pixel Y position
 * @returns Tile coordinates { tileX, tileY }
 */
export function pixelToTile(pixelX: number, pixelY: number): { tileX: number; tileY: number } {
  const world = screenToWorld(pixelX, pixelY);
  return {
    tileX: Math.floor(world.x),
    tileY: Math.floor(world.y),
  };
}

/**
 * Get tile center in screen coordinates
 * @param tileX Tile X coordinate
 * @param tileY Tile Y coordinate
 * @returns Screen position { x, y }
 */
export function tileToScreenCenter(tileX: number, tileY: number): { x: number; y: number } {
  return worldToScreen(tileX + 0.5, tileY + 0.5);
}

/**
 * Calculate depth for sorting isometric sprites
 * Higher values render in front (on top)
 * @param worldX Grid X position
 * @param worldY Grid Y position
 * @param worldZ Height/elevation
 * @param zOffset Additional Z offset for fine-tuning
 * @returns Depth value for sprite sorting
 */
export function calculateDepth(
  worldX: number,
  worldY: number,
  worldZ: number = 0,
  zOffset: number = 0
): number {
  // Base depth from position (x + y creates diagonal sorting)
  const baseDepth = worldX + worldY;
  // Z offset for entities above ground (scaled to avoid conflicts)
  const heightOffset = worldZ * 0.01;
  return baseDepth + heightOffset + zOffset;
}

/**
 * Calculate Manhattan distance between two grid positions
 */
export function gridDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  return Math.abs(x2 - x1) + Math.abs(y2 - y1);
}

/**
 * Calculate Euclidean distance between two grid positions
 */
export function euclideanDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get direction from one position to another (0-7 for 8 directions)
 * 0 = right, 1 = down-right, 2 = down, etc. (clockwise)
 */
export function getDirection(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
): number {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const angle = Math.atan2(dy, dx);
  // Convert angle to 8-direction index (0-7)
  return Math.round(((angle + Math.PI) / (2 * Math.PI)) * 8) % 8;
}

/**
 * Get normalized direction vector from direction index
 */
export function directionToVector(direction: number): { x: number; y: number } {
  const directions = [
    { x: 1, y: 0 },   // 0: right
    { x: 1, y: 1 },   // 1: down-right
    { x: 0, y: 1 },   // 2: down
    { x: -1, y: 1 },  // 3: down-left
    { x: -1, y: 0 },  // 4: left
    { x: -1, y: -1 }, // 5: up-left
    { x: 0, y: -1 },  // 6: up
    { x: 1, y: -1 },  // 7: up-right
  ];

  const dir = directions[direction % 8];
  // Normalize diagonal directions
  if (dir.x !== 0 && dir.y !== 0) {
    const length = Math.sqrt(2);
    return { x: dir.x / length, y: dir.y / length };
  }
  return dir;
}

/**
 * Check if a point is within a tile
 */
export function isPointInTile(
  pointX: number,
  pointY: number,
  tileX: number,
  tileY: number
): boolean {
  return (
    pointX >= tileX &&
    pointX < tileX + 1 &&
    pointY >= tileY &&
    pointY < tileY + 1
  );
}

/**
 * Get neighboring tile coordinates
 */
export function getNeighbors(
  tileX: number,
  tileY: number,
  includeDiagonals: boolean = true
): { x: number; y: number }[] {
  const neighbors = [
    { x: tileX + 1, y: tileY },     // right
    { x: tileX - 1, y: tileY },     // left
    { x: tileX, y: tileY + 1 },     // down
    { x: tileX, y: tileY - 1 },     // up
  ];

  if (includeDiagonals) {
    neighbors.push(
      { x: tileX + 1, y: tileY + 1 }, // down-right
      { x: tileX - 1, y: tileY + 1 }, // down-left
      { x: tileX + 1, y: tileY - 1 }, // up-right
      { x: tileX - 1, y: tileY - 1 }  // up-left
    );
  }

  return neighbors;
}

/**
 * Linear interpolation between two values
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
